/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import Button from 'components/base/Button';
import specificationService from 'service/specification';

interface CreateCharacteristicsProps {
  setOpenCharacter: (show: boolean) => void;
  setCharacterFormData?: any;
  characterFormData?: any;
}

const CreateCharacteristics = ({
  setOpenCharacter,
  setCharacterFormData,
  characterFormData
}: CreateCharacteristicsProps) => {
  const { t, i18n } = useTranslation();

  const { register, control, getValues, setValue, watch } = useForm();
  const selectedName = watch('name');

  const [allSpecifications, setAllSpecifications] = useState<any[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchSpecifications = async () => {
      setLoading(true);
      try {
        await specificationService
          .getList({
            status: true
          })
          .then((res: any) => {
            const allOptions = res?.specifications?.map((option: any) => ({
              value: option.id,
              pid: option.pid || 0,
              label: option.name?.[i18n?.language]
            }));
            setAllSpecifications(allOptions);
            setLoading(false);
          });
      } finally {
        setLoading(false);
      }
    };

    fetchSpecifications();
  }, [i18n.language]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allSpecifications;
    /* prettier-ignore */
    return allSpecifications.filter(option =>
      option.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allSpecifications, searchTerm]);

  // Show limited portion (lazy render)
  const displayedOptions = useMemo(() => {
    return filteredOptions.slice(0, visibleCount);
  }, [filteredOptions, visibleCount]);

  const handleLoadMore = () => {
    if (fetchingMore) return;
    if (visibleCount >= filteredOptions.length) return;
    setFetchingMore(true);
    setTimeout(() => {
      setVisibleCount(prev => prev + 10);
      setFetchingMore(false);
    }, 200);
  };

  const onSubmit = () => {
    const data = getValues();

    if (!data.name) {
      alert(t('please_select_characteristic')); // You can also use your custom alert system
      return;
    }

    const selectedSpecification = allSpecifications.find(
      (item: any) => item?.value === data.name?.value
    );
    const formDataItem = {
      character_name: data?.name?.label,
      values: data?.values,
      specification_id: data?.name?.value,
      specification_pid: selectedSpecification?.pid || 0
    };

    const updatedFormData = characterFormData.filter(
      (item: any) => item.specification_id !== formDataItem.specification_id
    );

    // Add the new item
    setCharacterFormData([...updatedFormData, formDataItem]);
    setOpenCharacter(false);
  };
  return (
    <div>
      <Form noValidate>
        <Row className="mb-2">
          <Col>
            <div className="react-select-container">
              <Form.Label>{t('characteristic')}</Form.Label>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    classNamePrefix="react-select"
                    options={displayedOptions}
                    styles={{
                      control: base => ({
                        ...base,
                        fontSize: '12.8px',
                        height: '48px'
                      }),
                      singleValue: base => ({
                        ...base,
                        fontSize: '12.8px'
                      }),
                      menuPortal: base => ({
                        ...base,
                        fontSize: '12.8px'
                      })
                    }}
                    placeholder={
                      loading ? t('loading') + '...' : t('characteristic')
                    }
                    isLoading={loading || fetchingMore}
                    isClearable
                    onInputChange={inputValue => {
                      setSearchTerm(inputValue);
                      setVisibleCount(10); // reset visible count on new search
                    }}
                    onMenuScrollToBottom={handleLoadMore}
                    onChange={(selected: any) => {
                      field.onChange(selected);
                      setValue('label', selected?.label || '');
                    }}
                    /* prettier-ignore */
                    noOptionsMessage={() =>
                      filteredOptions.length === 0
                        ? t('no_data_found') || 'No data found'
                        : displayedOptions.length >= filteredOptions.length
                          ? t('no_more_data') || 'No more data'
                          : t('loading') + '...'
                    }
                  />
                )}
              />
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <Form.Floating className="md-4">
              <Form.Control
                {...register('values')}
                type="text"
                placeholder={t('meanings')}
                onFocus={e => e.target.select()}
              />
              <label htmlFor="floatingInputCustom">{t('meanings')}</label>
            </Form.Floating>
          </Col>
        </Row>

        <Button
          className="w-100"
          variant="primary"
          type="button"
          onClick={onSubmit}
          disabled={!selectedName}
        >
          {t('save')}
        </Button>
      </Form>
    </div>
  );
};

export default CreateCharacteristics;
